import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbSpinnerModule,
} from "@nebular/theme";
import { AssetDocumentViewModel } from "@helix/client";
import { AssetDocumentUploadComponent } from "./asset-document-upload/asset-document-upload.component";
import { takeUntil } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState } from "../../../store/reducers";
import { ResetAssetDocumentNewChanges } from "./ngrx/asset-document-upload.actions";
import {
  AssetDocumentViewModelWithIcons,
  DocumentService,
} from "../../../services/document.service";
import { PermissionTypes } from "../../../enums/permissions.enum";
import { BehaviorSubject, Subject } from "rxjs";
import { TableModule } from "primeng/table";
import { PrimeTemplate } from "primeng/api";
import { MultiSelect } from "primeng/multiselect";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, DatePipe, NgIf } from "@angular/common";

@Component({
  selector: "ngx-asset-document-popup",
  templateUrl: "./asset-document-popup.component.html",
  styleUrls: ["./asset-document-popup.component.scss"],
  imports: [
    NbCardModule,
    NbSpinnerModule,
    TableModule,
    PrimeTemplate,
    MultiSelect,
    FormsModule,
    NbButtonModule,
    NgIf,
    AsyncPipe,
    DatePipe,
  ],
})
export class AssetDocumentPopupComponent implements OnInit, OnDestroy {
  @Input() assetId: number | null;
  @Input() assetName: string | null;
  private destroyed$ = new Subject<void>();
  assetDocuments$: Subject<AssetDocumentViewModelWithIcons[]> = new Subject<
    AssetDocumentViewModelWithIcons[]
  >();
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  hasDocumentCreateAccess$: BehaviorSubject<boolean>;
  documentUploadedByUsers$: BehaviorSubject<string[]> = new BehaviorSubject<
    string[]
  >([]);

  constructor(
    protected ref: NbDialogRef<any>,
    private readonly dialogService: NbDialogService,
    private store: Store<AppState>,
    private readonly documentService: DocumentService,
  ) {}
  ngOnInit(): void {
    this.getPermissions();
    this.listenForChanges();
    this.getDocumentUploadedBy();
    this.getAssetDocumentsWithIcons();
  }

  private getAssetDocumentsWithIcons() {
    this.documentService
      .getAssetDocumentsWithIcons(this.assetId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((assetDocuments) => {
        this.assetDocuments$.next(assetDocuments);
        this.isLoading$.next(false);
      });
  }

  private getPermissions() {
    this.hasDocumentCreateAccess$ = this.documentService.getDocumentPermissions(
      PermissionTypes.Create,
    );
  }

  private listenForChanges() {
    this.store
      .pipe(
        select((state) => state.assetDocumentUpload),
        takeUntil(this.destroyed$),
      )
      .subscribe((assetDocumentUpload) => {
        if (assetDocumentUpload.newChanges) {
          this.isLoading$.next(true);
          this.documentService.refreshAssetDocuments();
          this.store.dispatch(ResetAssetDocumentNewChanges());
        }
      });
  }

  private getDocumentUploadedBy() {
    this.assetDocuments$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((documents) => {
        const users: string[] = documents.map(
          (document) => document.documentUploadedBy,
        );
        const uniqueUsers = Array.from(new Set(users));
        this.documentUploadedByUsers$.next(uniqueUsers);
      });
  }

  uploadDocument(): void {
    const assetId = this.assetId;
    this.dialogService.open(AssetDocumentUploadComponent, {
      context: { assetId },
    });
  }

  getAssetDocument(document: AssetDocumentViewModel) {
    this.documentService
      .getAssetDocumentUrl(document)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((url) => {
        if (url) {
          this.triggerDownload(url);
        }
      });
  }

  private triggerDownload(url: string): void {
    const link = document.createElement("a");
    link.setAttribute("target", "_blank");
    link.href = url;
    link.click();
    link.remove();
  }

  close(): void {
    this.ref.close();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
